import axios from 'axios';
import {
  downloadAuthenticatedFile,
  downloadAuthenticatedPdf,
  viewAuthenticatedFile
} from './authenticatedPdf';

export const ATTACHMENT_SECTIONS = ['qualification', 'experience', 'training'];

/**
 * Upload one or more files to an authorization application's attachments endpoint.
 * @param {string} apiBase e.g. '/api/certification-authorization'
 * @param {string} applicationId
 * @param {File[]} files
 * @returns {Promise<object>} last response document
 */
export async function uploadApplicationAttachments(apiBase, applicationId, files = []) {
  let lastDoc = null;
  for (const file of files) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(
      `${apiBase}/${applicationId}/attachments`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    lastDoc = res.data;
  }
  return lastDoc;
}

export async function deleteApplicationAttachment(apiBase, applicationId, attachmentId) {
  const res = await axios.delete(`${apiBase}/${applicationId}/attachments/${attachmentId}`);
  return res.data;
}

/** Upload files for a form section: qualification | experience | training */
export async function uploadSectionAttachments(apiBase, applicationId, section, files = []) {
  let lastDoc = null;
  for (const file of files) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(
      `${apiBase}/${applicationId}/section-attachments/${section}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    lastDoc = res.data;
  }
  return lastDoc;
}

export async function deleteSectionAttachment(apiBase, applicationId, section, attachmentId) {
  const res = await axios.delete(
    `${apiBase}/${applicationId}/section-attachments/${section}/${attachmentId}`
  );
  return res.data;
}

export const ROW_ATTACHMENT_SECTIONS = ['experience', 'training'];

export function rowPendingKey(section, index) {
  return `${section}:${index}`;
}

/** Upload files for a specific experience/training row */
export async function uploadRowAttachments(apiBase, applicationId, section, rowId, files = []) {
  let lastDoc = null;
  for (const file of files) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(
      `${apiBase}/${applicationId}/row-attachments/${section}/${rowId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    lastDoc = res.data;
  }
  return lastDoc;
}

export async function deleteRowAttachment(apiBase, applicationId, section, rowId, attachmentId) {
  const res = await axios.delete(
    `${apiBase}/${applicationId}/row-attachments/${section}/${rowId}/${attachmentId}`
  );
  return res.data;
}

export function emptyPendingSectionFiles() {
  return { qualification: [], experience: [], training: [] };
}

export function emptyPendingRowFiles() {
  return {};
}

export function getExperienceRowsFromDoc(doc) {
  if (!doc) return [];
  if (Array.isArray(doc.recentExperience)) return doc.recentExperience;
  if (Array.isArray(doc.experienceDetails)) return doc.experienceDetails;
  return [];
}

export function getTrainingRowsFromDoc(doc) {
  if (!doc) return [];
  return Array.isArray(doc.trainingDetails) ? doc.trainingDetails : [];
}

export function getRowCollectionFromDoc(doc, section) {
  if (section === 'experience') return getExperienceRowsFromDoc(doc);
  if (section === 'training') return getTrainingRowsFromDoc(doc);
  return [];
}

/** After create/save, upload pending files keyed by `section:index`. */
export async function uploadPendingRowAttachments(apiBase, applicationId, doc, pendingRowFiles = {}) {
  let lastDoc = doc;
  const entries = Object.entries(pendingRowFiles || {});
  for (const [key, files] of entries) {
    if (!files?.length) continue;
    const [section, indexStr] = key.split(':');
    const index = Number(indexStr);
    if (!ROW_ATTACHMENT_SECTIONS.includes(section) || Number.isNaN(index)) continue;
    const rows = getRowCollectionFromDoc(lastDoc || doc, section);
    const row = rows[index];
    if (!row?._id) continue;
    const updated = await uploadRowAttachments(apiBase, applicationId, section, row._id, files);
    if (updated) lastDoc = updated;
  }
  return lastDoc;
}

/** Read section attachments from a saved application document. */
export function getSectionAttachmentsFromDoc(doc) {
  if (!doc) {
    return { qualification: [], experience: [], training: [] };
  }
  return {
    qualification: Array.isArray(doc.qualification?.attachments)
      ? doc.qualification.attachments
      : Array.isArray(doc.qualificationAttachments)
        ? doc.qualificationAttachments
        : [],
    experience: Array.isArray(doc.experienceAttachments) ? doc.experienceAttachments : [],
    training: Array.isArray(doc.trainingAttachments) ? doc.trainingAttachments : []
  };
}

/** View attachment in a new tab (auth header first; token URL fallback). */
export async function viewApplicationAttachment(att) {
  if (!att?.fileUrl) return;
  try {
    await viewAuthenticatedFile(att.fileUrl, att.mimeType || undefined);
  } catch {
    const token = localStorage.getItem('token');
    const sep = att.fileUrl.includes('?') ? '&' : '?';
    window.open(`${att.fileUrl}${sep}token=${encodeURIComponent(token || '')}`, '_blank');
  }
}

/** Force-download attachment for any allowed type (PDF / JPG / PNG). */
export async function downloadApplicationAttachment(att) {
  if (!att?.fileUrl) return;
  const filename = att.fileName || 'document';
  try {
    await downloadAuthenticatedFile(att.fileUrl, filename, att.mimeType || undefined);
  } catch {
    try {
      await downloadAuthenticatedPdf(att.fileUrl, filename);
    } catch {
      // Last resort: open with token so user can save from browser
      const token = localStorage.getItem('token');
      const sep = att.fileUrl.includes('?') ? '&' : '?';
      const a = document.createElement('a');
      a.href = `${att.fileUrl}${sep}token=${encodeURIComponent(token || '')}`;
      a.setAttribute('download', filename);
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }
}

/** Download a pending (not-yet-uploaded) local File from the create form. */
export function downloadPendingFile(file) {
  if (!(file instanceof File) && !(file instanceof Blob)) return;
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', file.name || 'document');
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
